import { type SupplierListItem } from '@choco-admin/types';
import { Progress, Spinner } from '@heroui/react';
import React, { useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { FiCamera, FiImage, FiUploadCloud } from 'react-icons/fi';

import { useNotification } from '../../../../contexts/NotificationContext';
import {
  MAX_IMAGE_SIZE_BYTES,
  useSupplierImageUpload,
  validateImageFile,
} from '../../hooks/useSupplierImageUpload';

interface CoverPhotoUploadProps {
  supplier: SupplierListItem;
}

type UploadTarget = 'cover' | 'logo';

const ACCEPTED_FILES = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/webp': ['.webp'],
};

const INVALID_FILE_MESSAGE =
  'Only JPEG, PNG, or WebP images up to 10 MB can be uploaded.';

/**
 * Cover-photo banner with an overlapping circular logo uploader, mirroring
 * the legacy supplier admin layout. Edit mode only — uploads go straight to
 * the presigned S3 URLs from the supplier detail query and are persisted
 * automatically server-side (no save mutation, independent of the form's
 * react-hook-form state).
 *
 * Uploads fire exclusively on explicit user action (drop / file pick). The
 * freshly uploaded file is kept as a local object-URL preview because the
 * server-side crops appear asynchronously; a delayed refetch picks them up.
 */
export const CoverPhotoUpload: React.FC<CoverPhotoUploadProps> = ({
  supplier,
}) => {
  const { addNotification } = useNotification();

  // Independent upload state per target so a cover upload does not show
  // progress on the logo (and vice versa).
  const coverUpload = useSupplierImageUpload({ supplierId: supplier.id });
  const logoUpload = useSupplierImageUpload({ supplierId: supplier.id });

  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  // Track created object URLs and pending refetch timers so both are cleaned
  // up on unmount.
  const objectUrlsRef = useRef<string[]>([]);
  const refetchTimersRef = useRef<number[]>([]);

  useEffect(() => {
    const objectUrls = objectUrlsRef.current;
    const refetchTimers = refetchTimersRef.current;
    return () => {
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
      refetchTimers.forEach((timer) => window.clearTimeout(timer));
    };
  }, []);

  const createPreviewUrl = (file: File): string => {
    const url = URL.createObjectURL(file);
    objectUrlsRef.current.push(url);
    return url;
  };

  const handleUpload = async (
    file: File,
    target: UploadTarget
  ): Promise<void> => {
    const uploader = target === 'cover' ? coverUpload : logoUpload;
    const label = target === 'cover' ? 'Cover photo' : 'Logo';

    // Dropzone already filters by type/size, but validate again so a clear
    // message is guaranteed regardless of how the file arrived.
    const validationError = validateImageFile(file);
    if (validationError) {
      addNotification({
        title: 'Invalid image',
        message: validationError,
        type: 'error',
      });
      return;
    }

    try {
      // Presigned URLs expire after ~15 minutes — always fetch a fresh one
      // right before uploading in case the form has been open for a while.
      const urls = await uploader.refreshUploadUrls();
      const uploadUrl =
        target === 'cover' ? urls.coverPhotoUploadUrl : urls.logoUploadUrl;

      if (!uploadUrl) {
        addNotification({
          title: 'Upload unavailable',
          message: `No upload destination is available for the ${label.toLowerCase()} on this distributor.`,
          type: 'error',
        });
        return;
      }

      await uploader.upload(file, uploadUrl);

      // Immediate local preview — server crops arrive asynchronously.
      const previewUrl = createPreviewUrl(file);
      if (target === 'cover') {
        setCoverPreview(previewUrl);
      } else {
        setLogoPreview(previewUrl);
      }

      addNotification({
        title: 'Uploaded',
        message: `${label} uploaded successfully. It may take a moment to process.`,
        type: 'success',
      });

      // Attempt to pick up the server-generated crops after a short delay;
      // the local preview stays in place if they are not ready yet.
      refetchTimersRef.current.push(
        window.setTimeout(() => {
          // Best-effort: if the refetch fails the local preview stays up and
          // the crops load on the next visit.
          uploader.refetchSupplierDetails().catch(() => undefined);
        }, 2000)
      );
    } catch (error) {
      addNotification({
        title: 'Upload failed',
        message:
          error instanceof Error
            ? error.message
            : `${label} upload failed. Please try again.`,
        type: 'error',
      });
    }
  };

  const handleRejected = () => {
    addNotification({
      title: 'Invalid image',
      message: INVALID_FILE_MESSAGE,
      type: 'error',
    });
  };

  const coverDropzone = useDropzone({
    accept: ACCEPTED_FILES,
    maxSize: MAX_IMAGE_SIZE_BYTES,
    multiple: false,
    disabled: coverUpload.isUploading,
    onDropAccepted: (files) => {
      // handleUpload never rejects — all failures surface as toasts.
      if (files[0]) handleUpload(files[0], 'cover');
    },
    onDropRejected: handleRejected,
  });

  const logoDropzone = useDropzone({
    accept: ACCEPTED_FILES,
    maxSize: MAX_IMAGE_SIZE_BYTES,
    multiple: false,
    disabled: logoUpload.isUploading,
    onDropAccepted: (files) => {
      // handleUpload never rejects — all failures surface as toasts.
      if (files[0]) handleUpload(files[0], 'logo');
    },
    onDropRejected: handleRejected,
  });

  const coverImageSrc =
    coverPreview ??
    supplier.coverPhoto?.mediumRectangle ??
    supplier.coverPhoto?.url ??
    null;
  const logoImageSrc =
    logoPreview ?? supplier.botUser?.profileImage?.mediumSquare ?? null;

  // The logo lives on the supplier's bot user; without one there is no
  // upload destination, so the avatar uploader is not offered.
  const hasBotUser = Boolean(supplier.botUser);

  return (
    <div className="mb-6">
      <label className="block text-sm font-medium text-foreground mb-2">
        Cover photo & logo
      </label>

      <div className={`relative ${hasBotUser ? 'mb-12' : 'mb-2'}`}>
        {/* Cover photo banner (750x400-ish, rectangle crops) */}
        <div
          {...coverDropzone.getRootProps()}
          aria-label="Upload cover photo"
          className={`
            group relative h-48 md:h-56 w-full overflow-hidden rounded-lg
            cursor-pointer transition-colors
            ${
              coverImageSrc
                ? 'bg-content2'
                : `border-2 border-dashed bg-default-50 ${
                    coverDropzone.isDragActive
                      ? 'border-primary bg-primary-50'
                      : 'border-default-300 hover:border-default-400 hover:bg-default-100'
                  }`
            }
            ${coverUpload.isUploading ? 'cursor-not-allowed' : ''}
          `}
        >
          <input {...coverDropzone.getInputProps()} />

          {coverImageSrc ? (
            <>
              <img
                src={coverImageSrc}
                alt={`${supplier.name ?? 'Distributor'} cover photo`}
                className="h-full w-full object-cover"
              />
              {/* Replace affordance on hover / drag */}
              {!coverUpload.isUploading && (
                <div
                  className={`
                    absolute inset-0 flex flex-col items-center justify-center gap-2
                    bg-background/70 transition-opacity
                    ${coverDropzone.isDragActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}
                  `}
                >
                  <FiUploadCloud className="h-6 w-6 text-foreground" />
                  <p className="text-sm font-medium text-foreground">
                    {coverDropzone.isDragActive
                      ? 'Drop to replace the cover photo'
                      : 'Replace cover photo'}
                  </p>
                </div>
              )}
            </>
          ) : (
            /* Empty state — no cover photo yet */
            <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center">
              <div className="p-3 bg-default-100 rounded-full">
                <FiImage className="h-6 w-6 text-default-600" />
              </div>
              <p className="text-sm font-medium text-foreground">
                {coverDropzone.isDragActive
                  ? 'Drop the image here'
                  : 'Add a cover photo'}
              </p>
              <p className="text-xs text-default-500">
                Drag & drop or click to browse — JPEG, PNG or WebP, max 10 MB
                (recommended 1125×600)
              </p>
            </div>
          )}

          {/* Upload progress overlay */}
          {coverUpload.isUploading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-background/80">
              <Progress
                aria-label="Cover photo upload progress"
                value={coverUpload.progress}
                size="sm"
                color="primary"
                className="w-1/2 max-w-xs"
              />
              <p className="text-sm text-default-500">
                Uploading… {coverUpload.progress}%
              </p>
            </div>
          )}
        </div>

        {/* Circular logo uploader, overlapping the banner's bottom-left */}
        {hasBotUser && (
          <div
            {...logoDropzone.getRootProps()}
            aria-label="Upload logo"
            className={`
              group/logo absolute -bottom-10 left-6 h-20 w-20 overflow-hidden
              rounded-full border-4 border-content1 bg-content2 shadow-md
              cursor-pointer
              ${logoUpload.isUploading ? 'cursor-not-allowed' : ''}
            `}
          >
            <input {...logoDropzone.getInputProps()} />

            {logoImageSrc ? (
              <img
                src={logoImageSrc}
                alt={`${supplier.name ?? 'Distributor'} logo`}
                className="h-full w-full object-cover"
              />
            ) : (
              /* Empty state — no logo yet */
              <div className="flex h-full w-full items-center justify-center">
                <FiCamera className="h-5 w-5 text-default-500" />
              </div>
            )}

            {logoUpload.isUploading ? (
              <div className="absolute inset-0 flex items-center justify-center bg-background/80">
                <Spinner size="sm" aria-label="Uploading logo" />
              </div>
            ) : (
              <div
                className={`
                  absolute inset-0 flex items-center justify-center
                  bg-background/70 transition-opacity
                  ${logoDropzone.isDragActive ? 'opacity-100' : 'opacity-0 group-hover/logo:opacity-100'}
                `}
              >
                <FiCamera className="h-5 w-5 text-foreground" />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Inline upload errors (toasts are transient; keep the last error visible) */}
      {coverUpload.error && (
        <p className="text-danger text-sm mt-1">{coverUpload.error}</p>
      )}
      {logoUpload.error && (
        <p className="text-danger text-sm mt-1">{logoUpload.error}</p>
      )}

      <p className="text-xs text-default-500 mt-1">
        Images are saved automatically after upload — no need to press Save
        Changes.
      </p>
    </div>
  );
};
